// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// WHERE — the postcode choropleth, and the finding this site exists for.
//
// Three decisions here are deliberate and load-bearing:
//
// 1. THE SCALE IS NOT A QUANTILE SCALE. Honours per head is savagely skewed:
//    the median rated postcode sits near 6 per 10,000 and the top one near 118.
//    Cut into deciles, the flat bottom two-thirds would be painted as if it
//    differed as much as the break-away top, and the map would show a gradient
//    that is not in the data. The breaks below are explicit, rounded and
//    human-readable, and the legend states how many postcodes fall in each.
//
// 2. A HOLE IS NOT A ZERO. Postcodes under the population floor are drawn in a
//    grey that is nowhere on the award ramp, and their tooltip says why. Painting
//    them as the palest gold would read as "nobody here was honoured", which is
//    a claim about people, and it is not one the data supports.
//
// 3. THE CAVEATS SIT ON THE MAP. Order of Australia only, 2010 onwards, placed
//    where the recipient was gazetted. A reader who screenshots the map takes
//    the caveats with them.

import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { loadBoundaries } from '../data';
import { dec, escapeHtml, money, num, pct, tipAttr } from '../format';
import { gloss } from '../glossary';
import { openOverlay, type OverlayHandle } from '../overlay';
import { RAMP, rampColour } from '../palette';
import type { Place, View } from '../types';

type MetricKey = 'crude' | 'aged';

interface Metric {
  id: MetricKey;
  button: string;
  /** "per 10,000 residents" — always spelled out next to a number. */
  unit: string;
  denomLabel: string;
  denom: (p: Place) => number;
  nationalDenom: number;
  /** Explicit, rounded cuts. breaks.length + 1 === RAMP.length. */
  breaks: number[];
}

/** Neutral greys, deliberately off the award ramp: these are "not rated". */
const FLOOR_FILL = '#b5b1a7';
const NODATA_FILL = '#e7e5e0';
const SELECT_STROKE = '#141821';

const GROUP_FLOOR = -1;
const GROUP_NODATA = -2;

const fmtBreak = (b: number): string => (Number.isInteger(b) ? num(b) : dec(b, 1));

function bandLabel(breaks: number[], i: number): string {
  if (i <= 0) return `under ${fmtBreak(breaks[0])}`;
  if (i >= breaks.length) return `${fmtBreak(breaks[breaks.length - 1])} or more`;
  return `${fmtBreak(breaks[i - 1])} to ${fmtBreak(breaks[i])}`;
}

/** The same band, short enough to sit under a bar at 375px without colliding. */
function bandTick(breaks: number[], i: number): string {
  if (i <= 0) return `<${fmtBreak(breaks[0])}`;
  if (i >= breaks.length) return `${fmtBreak(breaks[breaks.length - 1])}+`;
  return `${fmtBreak(breaks[i - 1])}–${fmtBreak(breaks[i])}`;
}

/** Same rule as rampColour, exposed as an index so legend/chart/map agree. */
function bandOf(v: number, breaks: number[]): number {
  if (!Number.isFinite(v)) return 0;
  let i = 0;
  while (i < breaks.length && v >= breaks[i]) i++;
  return i;
}

function pcOf(f?: GeoJSON.Feature): string {
  const props = f?.properties as { pc?: string } | null | undefined;
  return String(props?.pc ?? '');
}

export const whereView: View = (root, ctx) => {
  const { core, geo, meta } = ctx;

  const params = new URLSearchParams(root.dataset.params ?? '');
  let metric: MetricKey = params.get('metric') === 'aged' ? 'aged' : 'crude';
  /** A band index, GROUP_FLOOR, or null for "nothing selected". */
  let selected: number | null = null;
  let selectedPc: string | null = null;

  const METRICS: Record<MetricKey, Metric> = {
    crude: {
      id: 'crude',
      button: 'Per 10,000 residents',
      unit: 'per 10,000 residents',
      denomLabel: 'residents',
      denom: (p) => p.pop,
      nationalDenom: geo.national.pop,
      breaks: [2.5, 5, 10, 20, 40, 80],
    },
    aged: {
      id: 'aged',
      button: 'Per 10,000 aged 65+',
      unit: 'per 10,000 residents aged 65 and over',
      denomLabel: 'residents aged 65+',
      denom: (p) => p.o65,
      nationalDenom: geo.national.over65,
      breaks: [10, 25, 50, 100, 200, 400],
    },
  };

  const rateOf = (p: Place, m: Metric): number => {
    const d = m.denom(p);
    return Number.isFinite(d) && d > 0 ? (p.n / d) * 1e4 : NaN;
  };
  const nationalRate = (m: Metric): number =>
    m.nationalDenom > 0 ? (geo.national.awards / m.nationalDenom) * 1e4 : NaN;

  const byPc = new Map<string, Place>(geo.places.map((p) => [p.pc, p]));
  const eligible = geo.places.filter((p) => p.eligible === 1);
  const belowFloor = geo.places.filter((p) => p.eligible !== 1);
  const shadedAwards = eligible.reduce((a, p) => a + p.n, 0);
  const floorAwards = belowFloor.reduce((a, p) => a + p.n, 0);
  const endYear = meta.as_at_award_date.slice(0, 4);
  // Years are never run through num(): a thousands separator turns 2010 into "2,010".
  const fromYear = String(geo.fromYear);

  // Ranks over the eligible set, on each measure, computed once.
  const ranks: Record<MetricKey, Map<string, number>> = { crude: new Map(), aged: new Map() };
  for (const key of Object.keys(METRICS) as MetricKey[]) {
    const m = METRICS[key];
    [...eligible]
      .sort((a, b) => rateOf(b, m) - rateOf(a, m))
      .forEach((p, i) => ranks[key].set(p.pc, i + 1));
  }

  const bandStats = (m: Metric): { places: number; awards: number }[] => {
    const out = Array.from({ length: m.breaks.length + 1 }, () => ({ places: 0, awards: 0 }));
    for (const p of eligible) {
      const b = bandOf(rateOf(p, m), m.breaks);
      out[b].places += 1;
      out[b].awards += p.n;
    }
    return out;
  };

  /** Which legend group a postcode belongs to under the current metric. */
  const groupOf = (pc: string): number => {
    const p = byPc.get(pc);
    if (!p) return GROUP_NODATA;
    if (p.eligible !== 1) return GROUP_FLOOR;
    const m = METRICS[metric];
    return bandOf(rateOf(p, m), m.breaks);
  };

  // ---------------------------------------------------------------- scaffold

  root.innerHTML = `
    <div class="view-head">
      <h1>Where honours land</h1>
      <p class="lede">
        Honours are awarded person by person, but they do not fall evenly across the country.
        This is every ${gloss('order', 'Order of Australia')} award
        ${gloss('gazette', 'gazetted')} from ${fromYear} onwards that carries a published
        postcode — ${num(geo.national.awards)} of them — set against the people who live in that
        postcode at the 2021 Census.
      </p>
    </div>

    <div class="stats" id="w-stats"></div>

    <div class="note">
      <strong>What this map is, and what it is not.</strong>
      It is an <em>Order of Australia</em> map, not a map of the whole register: a postcode is
      published for ${pct(core.totals.oaWithPostcode / Math.max(1, core.totals.oa))} of Order of
      Australia records but only ${pct(core.totals.withPostcode / Math.max(1, core.totals.records))}
      of all ${num(core.totals.records)} awards, so no other award system can be mapped this way.
      It starts at ${fromYear} so the awards and the 2021 Census denominator describe roughly
      the same population. And each award sits where the recipient was gazetted — an address at the
      time of the award — which need not be where they did the work, lived earlier, or served.
    </div>

    <div class="controls" role="group" aria-label="Rate measure">
      <div class="control-group">
        <button type="button" data-metric="crude">${escapeHtml(METRICS.crude.button)}</button>
        <button type="button" data-metric="aged">${escapeHtml(METRICS.aged.button)}</button>
      </div>
      <span class="panel-sub" style="min-width:0">Identical geometry, identical awards — only the
        denominator changes.</span>
    </div>
    <p class="panel-sub" style="margin-bottom:var(--space-lg)">
      ${gloss('per-capita')} divides by everybody. ${gloss('age-controlled')} divides by the
      over-65s instead, because Order of Australia recipients skew elderly and an older suburb would
      otherwise look more honoured than it is. Central-business-district postcodes are the trap in
      the other direction: very few of their residents are over 65, so their age-controlled rate is
      unstable and runs very high.
    </p>

    <div class="panel">
      <div class="panel-head">
        <h2 id="w-map-title">Order of Australia awards per head, by postcode</h2>
        <p class="panel-sub" id="w-map-sub"></p>
      </div>
      <div class="controls" id="w-jump" aria-label="Zoom the map"></div>
      <div class="map-frame" id="w-map">
        <div class="loading">Loading postcode boundaries…</div>
      </div>
      <p class="panel-sub" style="margin-top:var(--space-md)" id="w-legend-note"></p>
      <div class="legend" id="w-legend"></div>
      <div class="note" id="w-floor-note"></div>
    </div>

    <div class="panel">
      <div class="panel-head">
        <h2>How the rated postcodes spread across the bands</h2>
        <p class="panel-sub" id="w-chart-sub"></p>
      </div>
      <div class="chart-wrap" id="w-chart"></div>
      <p class="panel-sub" style="margin-top:var(--space-md)">
        Bar height is the number of postcodes in the band; the tooltip adds how many awards sit
        there. Click a bar to highlight that band on the map.
      </p>
    </div>

    <div class="panel">
      <div class="panel-head">
        <h2 id="w-table-title">The postcodes at the top</h2>
        <p class="panel-sub" id="w-table-sub"></p>
      </div>
      <div class="scroller" id="w-table"></div>
    </div>
  `;

  const statsEl = root.querySelector<HTMLElement>('#w-stats')!;
  const mapSubEl = root.querySelector<HTMLElement>('#w-map-sub')!;
  const jumpEl = root.querySelector<HTMLElement>('#w-jump')!;
  const frameEl = root.querySelector<HTMLElement>('#w-map')!;
  const legendNoteEl = root.querySelector<HTMLElement>('#w-legend-note')!;
  const legendEl = root.querySelector<HTMLElement>('#w-legend')!;
  const floorNoteEl = root.querySelector<HTMLElement>('#w-floor-note')!;
  const chartSubEl = root.querySelector<HTMLElement>('#w-chart-sub')!;
  const chartEl = root.querySelector<HTMLElement>('#w-chart')!;
  const tableTitleEl = root.querySelector<HTMLElement>('#w-table-title')!;
  const tableSubEl = root.querySelector<HTMLElement>('#w-table-sub')!;
  const tableEl = root.querySelector<HTMLElement>('#w-table')!;

  // ------------------------------------------------------------- map state

  let disposed = false;
  let map: L.Map | null = null;
  let ro: ResizeObserver | null = null;
  let drawer: OverlayHandle | null = null;
  const layerByPc = new Map<string, L.Polygon>();
  let homeBounds: L.LatLngBounds | null = null;
  let noDataCount: number | null = null;

  // --------------------------------------------------------------- styling

  function styleFor(pc: string): L.PathOptions {
    const g = groupOf(pc);
    const dim = selected !== null && g !== selected;
    const isSelectedPc = selectedPc === pc;
    if (g === GROUP_NODATA) {
      return {
        fillColor: NODATA_FILL,
        fillOpacity: dim ? 0.12 : 0.55,
        color: isSelectedPc ? SELECT_STROKE : '#b9b5ac',
        weight: isSelectedPc ? 2.2 : 0.4,
        dashArray: '2 3',
      };
    }
    if (g === GROUP_FLOOR) {
      return {
        fillColor: FLOOR_FILL,
        fillOpacity: dim ? 0.14 : 0.7,
        color: isSelectedPc ? SELECT_STROKE : '#8d897f',
        weight: isSelectedPc ? 2.2 : 0.4,
        dashArray: '2 3',
      };
    }
    const p = byPc.get(pc)!;
    const m = METRICS[metric];
    return {
      fillColor: rampColour(rateOf(p, m), m.breaks),
      fillOpacity: dim ? 0.12 : 0.88,
      color: isSelectedPc ? SELECT_STROKE : '#ffffff',
      weight: isSelectedPc ? 2.4 : 0.4,
    };
  }

  function restyle(): void {
    for (const [pc, layer] of layerByPc) layer.setStyle(styleFor(pc));
  }

  // --------------------------------------------------------------- tooltips

  function tooltipHtml(pc: string): string {
    const p = byPc.get(pc);
    const safe = escapeHtml(pc);
    if (!p) {
      return `<strong>Postcode ${safe}</strong><br>Not rated — no Census resident population is
        published for this postal area above the floor.<br><em>Blank does not mean nobody here was
        honoured.</em>`;
    }
    if (p.eligible !== 1) {
      return `<strong>Postcode ${safe}</strong><br>${num(p.n)} Order of Australia award${
        p.n === 1 ? '' : 's'
      } since ${fromYear}<br>${num(p.pop)} residents · ${num(p.o65)} aged 65+<br>
        <em>Not rated: below the floor of ${num(geo.minPop)} residents and ${num(
          geo.minOver65,
        )} aged 65+.</em>`;
    }
    const c = rateOf(p, METRICS.crude);
    const a = rateOf(p, METRICS.aged);
    return `<strong>Postcode ${safe}</strong><br>${num(p.n)} Order of Australia award${
      p.n === 1 ? '' : 's'
    } since ${fromYear}<br>${num(p.pop)} residents · <strong>${dec(
      c,
      1,
    )}</strong> per 10,000<br>${num(p.o65)} aged 65+ · <strong>${dec(
      a,
      1,
    )}</strong> per 10,000 aged 65+<br><em>Click for detail</em>`;
  }

  // ----------------------------------------------------------------- drawer

  function zoomTo(pc: string): void {
    const layer = layerByPc.get(pc);
    if (!map || !layer) return;
    const b = layer.getBounds();
    if (b.isValid()) map.flyToBounds(b, { maxZoom: 12, padding: [40, 40] });
  }

  function drawerBody(pc: string): string {
    const safe = escapeHtml(pc);
    const p = byPc.get(pc);
    const zoomBtn = layerByPc.has(pc)
      ? `<p><button type="button" class="pill" data-zoom style="min-height:40px">Zoom the map to
          postcode ${safe}</button></p>`
      : '';

    if (!p) {
      return `<p class="panel-sub">Postcode ${safe} is not rated on this map.</p>
        <p>No Census resident population is published for this postal area above the floor of
        ${num(geo.minPop)} residents, so no rate can be calculated for it.</p>
        <div class="note">A postcode with no shading is not a postcode with no honoured residents.
        It is a postcode this map declines to rate.</div>${zoomBtn}`;
    }

    const crude = rateOf(p, METRICS.crude);
    const aged = rateOf(p, METRICS.aged);
    const natC = nationalRate(METRICS.crude);
    const natA = nationalRate(METRICS.aged);
    const facts = `
      <div class="scroller"><table>
        <tbody>
          <tr><th>Order of Australia awards, ${fromYear}–${escapeHtml(endYear)}</th>
              <td class="n">${num(p.n)}</td></tr>
          <tr><th>Residents (2021 Census)</th><td class="n">${num(p.pop)}</td></tr>
          <tr><th>Residents aged 65 and over</th><td class="n">${num(p.o65)}</td></tr>
          <tr><th>Median weekly household income</th>
              <td class="n">${p.inc == null ? '—' : `${money(p.inc)} a week`}</td></tr>
          <tr><th>Median age</th><td class="n">${p.age == null ? '—' : num(p.age)}</td></tr>
        </tbody>
      </table></div>`;

    if (p.eligible !== 1) {
      return `<p class="panel-sub">Postcode ${safe} is <strong>not rated</strong>.</p>
        <p>Rating a postcode needs at least ${num(geo.minPop)} residents and ${num(
          geo.minOver65,
        )} residents aged 65 and over. With ${num(p.pop)} residents and ${num(
          p.o65,
        )} aged 65+, one extra award here would move its rate by more than the whole spread of the
        map, so the number would be noise dressed as a finding.</p>
        ${facts}
        <div class="note">${num(p.n)} Order of Australia award${
          p.n === 1 ? ' was' : 's were'
        } gazetted to this postcode. Not rated is not the same as none.</div>${zoomBtn}`;
    }

    const ratioC = natC > 0 ? crude / natC : NaN;
    const ratioA = natA > 0 ? aged / natA : NaN;
    const rC = ranks.crude.get(pc);
    const rA = ranks.aged.get(pc);
    const cmp = (r: number): string =>
      !Number.isFinite(r)
        ? 'not comparable'
        : r >= 1
          ? `${dec(r, 2)}× the national rate`
          : `${dec(r, 2)}× the national rate (below it)`;

    return `
      <div class="stats">
        <div class="stat" data-tip="${tipAttr(
          `${p.n} Order of Australia awards ÷ ${p.pop} residents × 10,000`,
        )}">
          <div class="stat-value">${dec(crude, 1)}</div>
          <div class="stat-label">per 10,000 residents · rank ${
            rC ? `${num(rC)} of ${num(eligible.length)}` : '—'
          }</div>
        </div>
        <div class="stat" data-tip="${tipAttr(
          `${p.n} Order of Australia awards ÷ ${p.o65} residents aged 65+ × 10,000`,
        )}">
          <div class="stat-value">${dec(aged, 1)}</div>
          <div class="stat-label">per 10,000 aged 65+ · rank ${
            rA ? `${num(rA)} of ${num(eligible.length)}` : '—'
          }</div>
        </div>
        <div class="stat" data-tip="${tipAttr(
          `National: ${geo.national.awards} awards ÷ ${geo.national.pop} residents × 10,000 = ${dec(
            natC,
            2,
          )}`,
        )}">
          <div class="stat-value">${dec(ratioC, 2)}×</div>
          <div class="stat-label">against the national rate of ${dec(natC, 2)} per 10,000</div>
        </div>
      </div>
      <p>Postcode ${safe} holds ${num(p.n)} Order of Australia award${
        p.n === 1 ? '' : 's'
      } gazetted since ${fromYear}. Per resident that is ${cmp(
        ratioC,
      )} of ${dec(natC, 2)} per 10,000; per resident aged 65 and over it is ${cmp(
        ratioA,
      )} of ${dec(natA, 2)} per 10,000 aged 65+.</p>
      ${facts}
      <div class="note">Awards are placed where the recipient was gazetted, not where the service
      was given, and a postcode is published for only some records. Treat this as a rate for a
      place, never as a statement about any individual.</div>
      ${zoomBtn}`;
  }

  function openPlace(pc: string): void {
    // Close first: the old drawer's onClose clears the selection, and it must
    // not run after the new one has set it.
    drawer?.close();
    selectedPc = pc;
    restyle();
    const handle = openOverlay({
      title: `Postcode ${pc}`,
      kind: 'drawer',
      body: drawerBody(pc),
      onClose: () => {
        drawer = null;
        selectedPc = null;
        if (!disposed) restyle();
      },
    });
    drawer = handle;
    handle.panel.querySelector<HTMLButtonElement>('[data-zoom]')?.addEventListener('click', () => {
      handle.close();
      zoomTo(pc);
    });
  }

  // ------------------------------------------------------------------ stats

  function renderStats(): void {
    const m = METRICS[metric];
    const nat = nationalRate(m);
    const rated = [...eligible].sort((a, b) => rateOf(b, m) - rateOf(a, m));
    const top = rated[0];
    const topRate = top ? rateOf(top, m) : NaN;
    const ratio = Number.isFinite(nat) && nat > 0 ? topRate / nat : NaN;

    const tiles: { v: string; l: string; tip: string }[] = [
      {
        v: dec(nat, 2),
        l: `national rate, ${m.unit}`,
        tip: `${num(geo.national.awards)} Order of Australia awards ${geo.fromYear}+ ÷ ${num(
          m.nationalDenom,
        )} ${m.denomLabel} × 10,000`,
      },
      {
        v: top ? dec(topRate, 1) : '—',
        l: top ? `highest rated postcode: ${top.pc} (${num(top.n)} awards)` : 'no rated postcodes',
        tip: top
          ? `Postcode ${top.pc}: ${num(top.n)} awards, ${num(m.denom(top))} ${m.denomLabel}`
          : 'No postcode clears the population floor',
      },
      {
        v: Number.isFinite(ratio) ? `${dec(ratio, 1)}×` : '—',
        l: 'the top postcode against the national rate',
        tip: `${dec(topRate, 2)} ÷ ${dec(nat, 2)} = ${dec(ratio, 2)}`,
      },
      {
        v: num(eligible.length),
        l: `postcodes rated, holding ${num(shadedAwards)} awards (${pct(
          shadedAwards / Math.max(1, geo.national.awards),
        )} of the mapped awards)`,
        tip: `${num(eligible.length)} of ${num(
          geo.places.length,
        )} postcodes with data clear the floor of ${num(geo.minPop)} residents and ${num(
          geo.minOver65,
        )} aged 65+`,
      },
    ];

    statsEl.innerHTML = tiles
      .map(
        (t) => `<div class="stat" data-tip="${tipAttr(t.tip)}" aria-label="${escapeHtml(
          `${t.v}: ${t.l}`,
        )}">
          <div class="stat-value">${escapeHtml(t.v)}</div>
          <div class="stat-label">${escapeHtml(t.l)}</div>
        </div>`,
      )
      .join('');
  }

  // ----------------------------------------------------------------- legend

  function renderLegend(): void {
    const m = METRICS[metric];
    const stats = bandStats(m);
    const nat = nationalRate(m);
    const natBand = bandOf(nat, m.breaks);

    const items = stats.map((s, i) => {
      const on = selected === i;
      const label = bandLabel(m.breaks, i);
      const tip = `${label} ${m.unit}\n${num(s.places)} postcodes · ${num(s.awards)} awards\n${
        on ? 'Click again to clear the highlight' : 'Click to highlight this band on the map'
      }`;
      return `<button type="button" class="legend-item" data-band="${i}"
        aria-pressed="${on ? 'true' : 'false'}"
        data-tip="${tipAttr(tip)}"
        aria-label="${escapeHtml(`${label} ${m.unit}: ${s.places} postcodes`)}"
        style="background:none;border:0;padding:2px 4px;font:inherit;font-size:var(--font-size-sm);
          color:inherit;cursor:pointer;min-height:40px;border-radius:4px;min-width:0;
          ${on ? 'outline:2px solid var(--accent-secondary);outline-offset:1px;' : ''}">
        <span class="swatch" style="background:${RAMP[i]}"></span>
        <span style="min-width:0">${escapeHtml(label)}
          <span class="num" style="color:var(--text-secondary)">(${num(s.places)})</span>${
            i === natBand
              ? ` <span class="num" style="color:var(--accent-secondary)">◂ national ${dec(
                  nat,
                  1,
                )}</span>`
              : ''
          }</span>
      </button>`;
    });

    const floorTip = `${num(belowFloor.length)} postcodes hold ${num(
      floorAwards,
    )} awards but have fewer than ${num(geo.minPop)} residents or fewer than ${num(
      geo.minOver65,
    )} aged 65+\nClick to highlight them on the map`;
    items.push(`<button type="button" class="legend-item" data-band="${GROUP_FLOOR}"
      aria-pressed="${selected === GROUP_FLOOR ? 'true' : 'false'}"
      data-tip="${tipAttr(floorTip)}"
      aria-label="Too few residents to rate: ${belowFloor.length} postcodes"
      style="background:none;border:0;padding:2px 4px;font:inherit;font-size:var(--font-size-sm);
        color:inherit;cursor:pointer;min-height:40px;border-radius:4px;min-width:0;
        ${
          selected === GROUP_FLOOR
            ? 'outline:2px solid var(--accent-secondary);outline-offset:1px;'
            : ''
        }">
      <span class="swatch" style="background:${FLOOR_FILL}"></span>
      <span style="min-width:0">too few residents to rate
        <span class="num" style="color:var(--text-secondary)">(${num(belowFloor.length)})</span></span>
    </button>`);

    items.push(`<span class="legend-item" data-tip="${tipAttr(
      'Postal areas with no published Census resident population above the floor — mostly PO-box and industrial postcodes\nNot rated, and not zero',
    )}">
      <span class="swatch" style="background:${NODATA_FILL};border-style:dashed"></span>
      <span style="min-width:0">no rated population published
        <span class="num" style="color:var(--text-secondary)">(${
          noDataCount === null ? '…' : num(noDataCount)
        })</span></span>
    </span>`);

    legendEl.innerHTML = items.join('');
    legendNoteEl.innerHTML = `<strong>Fixed bands, ${escapeHtml(
      m.unit,
    )}</strong> — rounded cuts, not deciles. A quantile scale would give the flat bottom of this
      distribution the same visual range as the break-away top and invent a gradient that is not
      there. The number in brackets is how many rated postcodes fall in the band; the national rate
      of ${dec(nat, 2)} sits in the “${escapeHtml(bandLabel(m.breaks, natBand))}” band.`;

    floorNoteEl.innerHTML = `<strong>The floor, and why blank is not zero.</strong>
      A postcode is rated only if it has at least ${num(geo.minPop)} residents and ${num(
        geo.minOver65,
      )} residents aged 65 and over — otherwise a single award swings the rate further than the
      entire spread of the map. ${num(belowFloor.length)} postcodes fall short, and between them
      they still hold ${num(floorAwards)} Order of Australia awards; they are drawn in flat grey,
      not in the palest gold, because grey says “not rated” and pale gold would say “nobody here”.`;
  }

  // ------------------------------------------------------------------ chart

  function renderChart(): void {
    const m = METRICS[metric];
    const stats = bandStats(m);
    const nat = nationalRate(m);
    const natBand = bandOf(nat, m.breaks);
    const totalAwards = Math.max(1, shadedAwards);

    chartSubEl.textContent = `Each bar is one band of the legend above: how many of the ${eligible.length.toLocaleString(
      'en-AU',
    )} rated postcodes sit in it, ${m.unit}. The distribution is long-tailed — that is why the map uses fixed bands rather than deciles.`;

    const maxC = Math.max(...stats.map((s) => s.places), 1);
    const niceMax = Math.max(10, Math.ceil(maxC / 50) * 50);
    // Authored near the width this chart actually renders at (~1200px inside
    // the panel). An SVG with width="100%" scales its coordinate system, TEXT
    // INCLUDED, so a 720-unit canvas stretched to 1214px rendered its 20px
    // labels at 34px -- legible, but half again the size of the type on every
    // other chart on the site.
    const W = 1200;
    const H = 400;
    const ml = 96;
    const mr = 24;
    const mt = 40;
    const mb = 112;
    const pw = Math.max(1, W - ml - mr);
    const ph = Math.max(1, H - mt - mb);
    const slots = Math.max(1, stats.length);
    const sw = pw / slots;
    const barW = Math.max(1, sw * 0.72);

    const yTicks = [0, niceMax / 2, niceMax];
    const gridline = yTicks
      .map((t) => {
        const y = mt + ph - (t / niceMax) * ph;
        return `<line x1="${ml}" y1="${y.toFixed(1)}" x2="${(ml + pw).toFixed(1)}" y2="${y.toFixed(
          1,
        )}" stroke="var(--border-subtle)" stroke-width="1"/>
        <text x="${ml - 8}" y="${(y + 7).toFixed(1)}" text-anchor="end" font-size="20">${num(
          t,
        )}</text>`;
      })
      .join('');

    const bars = stats
      .map((s, i) => {
        const x = ml + i * sw + (sw - barW) / 2;
        const h = niceMax > 0 ? (s.places / niceMax) * ph : 0;
        const y = mt + ph - h;
        const dim = selected !== null && selected !== i;
        const label = bandLabel(m.breaks, i);
        const tip = `${label} ${m.unit}\n${num(s.places)} postcodes\n${num(s.awards)} awards (${pct(
          s.awards / totalAwards,
        )} of rated awards)\nClick to highlight this band on the map`;
        const cx = x + barW / 2;
        return `<g class="w-bar" data-band="${i}" role="button" tabindex="0"
            aria-label="${escapeHtml(`${label}: ${s.places} postcodes, ${s.awards} awards`)}"
            data-tip="${tipAttr(tip)}" style="cursor:pointer">
            <rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${barW.toFixed(
              1,
            )}" height="${Math.max(0, h).toFixed(1)}" fill="${RAMP[i]}"
              stroke="${selected === i ? SELECT_STROKE : '#ffffff'}" stroke-width="${
                selected === i ? 2 : 0.8
              }" opacity="${dim ? 0.3 : 1}"/>
            <text x="${cx.toFixed(1)}" y="${(y - 6).toFixed(
              1,
            )}" text-anchor="middle" font-size="20" opacity="${dim ? 0.4 : 1}">${num(
              s.places,
            )}</text>
            <text x="${cx.toFixed(1)}" y="${(mt + ph + 24).toFixed(
              1,
            )}" text-anchor="middle" font-size="20" opacity="${dim ? 0.4 : 1}">${escapeHtml(
              bandTick(m.breaks, i),
            )}</text>
            ${
              i === natBand
                ? `<path d="M ${(cx - 7).toFixed(1)} ${(mt + ph + 42).toFixed(1)} L ${(
                    cx + 7
                  ).toFixed(1)} ${(mt + ph + 42).toFixed(1)} L ${cx.toFixed(1)} ${(
                    mt +
                    ph +
                    31
                  ).toFixed(1)} Z" fill="var(--accent-secondary)"/>`
                : ''
            }
          </g>`;
      })
      .join('');

    chartEl.innerHTML = `
      <svg class="chart" viewBox="0 0 ${W} ${H}" width="100%" role="group"
           aria-label="Rated postcodes in each rate band, ${escapeHtml(m.unit)}">
        <text x="0" y="16" font-size="20" fill="var(--text-secondary)">postcodes</text>
        <text x="${ml + pw}" y="16" text-anchor="end" font-size="20"
              fill="var(--accent-secondary)">▲ national rate ${dec(nat, 2)}</text>
        ${gridline}
        <line x1="${ml}" y1="${mt + ph}" x2="${ml + pw}" y2="${
          mt + ph
        }" stroke="var(--border-default)" stroke-width="1.5"/>
        ${bars}
        <text x="${(ml + pw / 2).toFixed(1)}" y="${
          H - 8
        }" text-anchor="middle" font-size="20">rate band — Order of Australia awards ${escapeHtml(
          m.unit,
        )}</text>
      </svg>`;
  }

  // ------------------------------------------------------------------ table

  function renderTable(): void {
    const m = METRICS[metric];
    const other = METRICS[metric === 'crude' ? 'aged' : 'crude'];

    if (selected === GROUP_FLOOR) {
      const rows = [...belowFloor].sort((a, b) => b.n - a.n).slice(0, 20);
      tableTitleEl.textContent = 'Postcodes with awards but too few residents to rate';
      tableSubEl.textContent = `The 20 below-floor postcodes holding the most awards. They are grey on the map because a rate built on fewer than ${geo.minPop.toLocaleString(
        'en-AU',
      )} residents or ${geo.minOver65.toLocaleString('en-AU')} residents aged 65+ would be noise — not because nobody there was honoured.`;
      tableEl.innerHTML = `<table>
        <thead><tr><th>Postcode</th><th class="n">Awards</th><th class="n">Residents</th>
          <th class="n">Aged 65+</th><th>Why it is not rated</th></tr></thead>
        <tbody>${rows
          .map((p) => {
            const why =
              p.pop < geo.minPop && p.o65 < geo.minOver65
                ? 'under both floors'
                : p.pop < geo.minPop
                  ? 'under the resident floor'
                  : 'under the 65+ floor';
            return `<tr data-row data-pc="${escapeHtml(p.pc)}">
              <td><button type="button" class="pill" style="min-height:40px">${escapeHtml(
                p.pc,
              )}</button></td>
              <td class="n">${num(p.n)}</td><td class="n">${num(p.pop)}</td>
              <td class="n">${num(p.o65)}</td><td>${why}</td></tr>`;
          })
          .join('')}</tbody></table>`;
      return;
    }

    const pool =
      selected === null
        ? eligible
        : eligible.filter((p) => bandOf(rateOf(p, m), m.breaks) === selected);
    const rows = [...pool].sort((a, b) => rateOf(b, m) - rateOf(a, m)).slice(0, 20);

    tableTitleEl.textContent =
      selected === null
        ? 'The postcodes at the top'
        : `Postcodes in the “${bandLabel(m.breaks, selected)}” band`;
    tableSubEl.textContent =
      (selected === null
        ? `The 20 highest-rating postcodes of the ${eligible.length.toLocaleString('en-AU')} rated, `
        : `The 20 highest-rating of ${pool.length.toLocaleString('en-AU')} postcodes in this band, `) +
      `ranked ${m.unit}. Rank is against all rated postcodes. Select a row for the full detail.`;

    if (rows.length === 0) {
      tableEl.innerHTML = '<p class="empty">No rated postcodes in this band.</p>';
      return;
    }

    tableEl.innerHTML = `<table>
      <thead><tr>
        <th class="n">Rank</th><th>Postcode</th><th class="n">Awards</th>
        <th class="n">${escapeHtml(m.button)}</th><th class="n">${escapeHtml(other.button)}</th>
        <th class="n">Residents</th><th class="n">Aged 65+</th>
        <th class="n">Median weekly household income</th><th class="n">Median age</th>
      </tr></thead>
      <tbody>${rows
        .map((p) => {
          const r = ranks[metric].get(p.pc);
          const tip = `Postcode ${p.pc}: ${p.n} awards ÷ ${m.denom(p)} ${m.denomLabel} × 10,000 = ${dec(
            rateOf(p, m),
            2,
          )}`;
          return `<tr data-row data-pc="${escapeHtml(p.pc)}" data-tip="${tipAttr(tip)}">
            <td class="n">${r ? num(r) : '—'}</td>
            <td><button type="button" class="pill" style="min-height:40px"
                aria-label="Open detail for postcode ${escapeHtml(p.pc)}">${escapeHtml(
                  p.pc,
                )}</button></td>
            <td class="n">${num(p.n)}</td>
            <td class="n">${dec(rateOf(p, m), 1)}</td>
            <td class="n">${dec(rateOf(p, other), 1)}</td>
            <td class="n">${num(p.pop)}</td>
            <td class="n">${num(p.o65)}</td>
            <td class="n">${p.inc == null ? '—' : money(p.inc)}</td>
            <td class="n">${p.age == null ? '—' : num(p.age)}</td>
          </tr>`;
        })
        .join('')}</tbody></table>`;
  }

  // ----------------------------------------------------------- interactions

  function setBand(b: number): void {
    selected = selected === b ? null : b;
    renderLegend();
    renderChart();
    renderTable();
    restyle();
  }

  function setMetric(next: MetricKey): void {
    if (next === metric) return;
    metric = next;
    // replaceState, not ctx.go: a hashchange would tear the whole view down and
    // rebuild the map, throwing away the reader's pan and zoom.
    history.replaceState(null, '', `#/where${metric === 'crude' ? '' : `?metric=${metric}`}`);
    syncButtons();
    renderStats();
    renderLegend();
    renderChart();
    renderTable();
    restyle();
  }

  function syncButtons(): void {
    root.querySelectorAll<HTMLButtonElement>('[data-metric]').forEach((b) => {
      b.setAttribute('aria-pressed', b.dataset.metric === metric ? 'true' : 'false');
    });
    mapSubEl.innerHTML = `Order of Australia awards gazetted ${fromYear}–${escapeHtml(
      endYear,
    )}, ${escapeHtml(
      METRICS[metric].unit,
    )}, by 2021 postal area. Hover any postcode for its numbers; select one for its rank and detail.`;
  }

  root.querySelectorAll<HTMLButtonElement>('[data-metric]').forEach((b) => {
    b.addEventListener('click', () => setMetric(b.dataset.metric === 'aged' ? 'aged' : 'crude'));
  });

  legendEl.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLElement>('[data-band]');
    if (btn) setBand(Number(btn.dataset.band));
  });

  chartEl.addEventListener('click', (e) => {
    const g = (e.target as Element).closest<HTMLElement>('[data-band]');
    if (g) setBand(Number(g.dataset.band));
  });
  chartEl.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const g = (e.target as Element).closest<HTMLElement>('[data-band]');
    if (g) {
      e.preventDefault();
      setBand(Number(g.dataset.band));
    }
  });

  tableEl.addEventListener('click', (e) => {
    const tr = (e.target as Element).closest<HTMLElement>('tr[data-pc]');
    if (!tr?.dataset.pc) return;
    openPlace(tr.dataset.pc);
    zoomTo(tr.dataset.pc);
  });

  syncButtons();
  renderStats();
  renderLegend();
  renderChart();
  renderTable();

  // -------------------------------------------------------------- the map

  void (async () => {
    let fc: GeoJSON.FeatureCollection;
    try {
      fc = await loadBoundaries();
    } catch (err) {
      if (!disposed) {
        frameEl.innerHTML = `<div class="error" role="alert">Could not load the postcode
          boundaries. ${escapeHtml((err as Error).message)}</div>`;
      }
      return;
    }
    if (disposed) return;

    frameEl.innerHTML = '';
    const canvas = document.createElement('div');
    canvas.style.height = '100%';
    canvas.style.width = '100%';
    frameEl.appendChild(canvas);

    // Canvas renderer: 2,644 postal areas as SVG paths is more DOM than a phone
    // wants to keep alive.
    const m = L.map(canvas, {
      preferCanvas: true,
      minZoom: 3,
      maxZoom: 12,
      zoomControl: true,
      scrollWheelZoom: false, // never hijack the page scroll
      attributionControl: true,
    });
    map = m;
    m.attributionControl.setPrefix(false);

    // Build the boundary layer BEFORE anything is added to the map. A tile layer
    // added to a map that has no view yet throws "Set map center and zoom first",
    // and the only honest source for that first view is the geometry itself.
    const geoLayer = L.geoJSON(fc, {
      attribution:
        'Boundaries: ABS Postal Areas 2021 (CC BY 4.0) · Honours: PM&amp;C (CC BY 4.0) · Population &amp; income: ABS 2021 Census',
      style: (f) => styleFor(pcOf(f)),
      onEachFeature: (f, layer) => {
        const pc = pcOf(f);
        const poly = layer as L.Polygon;
        layerByPc.set(pc, poly);
        poly.bindTooltip(tooltipHtml(pc), { sticky: true });
        poly.on('mouseover', () => {
          poly.setStyle({ weight: 2, color: SELECT_STROKE });
        });
        poly.on('mouseout', () => {
          poly.setStyle(styleFor(pc));
        });
        poly.on('click', () => openPlace(pc));
      },
    });

    noDataCount = [...layerByPc.keys()].filter((pc) => !byPc.has(pc)).length;
    renderLegend();

    // Home view. Australia's external territories (Cocos, Christmas, Norfolk,
    // Lord Howe) sit up to 40 degrees of longitude off the continent, and fitting
    // to them leaves the mainland a smudge. Trim the extreme 0.5% of postcode
    // centres by longitude — derived from the real geometry, nothing typed in —
    // and frame what is left. Everything stays on the map and stays pannable.
    const centres: { pc: string; lng: number }[] = [];
    for (const [pc, poly] of layerByPc) {
      const b = poly.getBounds();
      if (b.isValid()) centres.push({ pc, lng: b.getCenter().lng });
    }
    centres.sort((a, b) => a.lng - b.lng);
    const trim = Math.floor(centres.length * 0.005);
    const keep = centres.slice(trim, Math.max(trim + 1, centres.length - trim));
    let home: L.LatLngBounds | null = null;
    for (const c of keep) {
      const b = layerByPc.get(c.pc)?.getBounds();
      if (!b || !b.isValid()) continue;
      home = home ? home.extend(b) : L.latLngBounds(b.getSouthWest(), b.getNorthEast());
    }
    const all = geoLayer.getBounds();
    homeBounds = home && home.isValid() ? home : all.isValid() ? all : null;
    if (!homeBounds) {
      m.remove();
      map = null;
      frameEl.innerHTML = `<div class="error" role="alert">The postcode boundaries loaded but
        carry no usable geometry, so there is nothing to map.</div>`;
      return;
    }

    // View first, then layers: a tile layer on a view-less map throws.
    m.fitBounds(homeBounds, { padding: [10, 10] });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      subdomains: 'abcd',
      minZoom: 3,
      maxZoom: 12,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors, ' +
        '&copy; <a href="https://carto.com/attributions" target="_blank" rel="noopener">CARTO</a>',
    }).addTo(m);
    geoLayer.addTo(m);

    // The caveats travel with the map, not with the page around it.
    const caption = new L.Control({ position: 'bottomleft' });
    caption.onAdd = (): HTMLElement => {
      const el = L.DomUtil.create('div');
      el.style.cssText =
        'background:rgba(255,255,255,0.93);border:1px solid rgba(20,24,33,0.18);' +
        'border-radius:6px;padding:6px 8px;max-width:min(280px,58vw);font-size:11px;' +
        'line-height:1.35;color:#2b2f38;';
      el.innerHTML =
        `<strong>Order of Australia only</strong>, ${fromYear}–${escapeHtml(endYear)}: ` +
        `${num(shadedAwards)} awards across ${num(eligible.length)} rated postcodes. ` +
        `Placed where the recipient was gazetted, not where the service was given. ` +
        `Grey = too few residents to rate, not zero.`;
      L.DomEvent.disableClickPropagation(el);
      L.DomEvent.disableScrollPropagation(el);
      return el;
    };
    caption.addTo(m);

    // Jump controls, centred on real postcode geometry rather than typed
    // coordinates.
    const CITIES: { name: string; pc: string }[] = [
      { name: 'Sydney', pc: '2000' },
      { name: 'Melbourne', pc: '3000' },
      { name: 'Brisbane', pc: '4000' },
      { name: 'Perth', pc: '6000' },
      { name: 'Adelaide', pc: '5000' },
      { name: 'Hobart', pc: '7000' },
      { name: 'Canberra', pc: '2600' },
      { name: 'Darwin', pc: '0800' },
    ];
    jumpEl.innerHTML = '';
    const homeBtn = document.createElement('button');
    homeBtn.type = 'button';
    homeBtn.className = 'pill';
    homeBtn.style.minHeight = '40px';
    homeBtn.textContent = 'Whole country';
    homeBtn.setAttribute('data-tip', 'Fit the map back to the whole country');
    homeBtn.addEventListener('click', () => {
      if (homeBounds) m.fitBounds(homeBounds, { padding: [10, 10] });
    });
    jumpEl.appendChild(homeBtn);
    for (const c of CITIES) {
      const poly = layerByPc.get(c.pc);
      if (!poly) continue;
      const b = poly.getBounds();
      if (!b.isValid()) continue;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pill';
      btn.style.minHeight = '40px';
      btn.textContent = c.name;
      btn.setAttribute('data-tip', `Zoom to the ${c.name} area (centred on postcode ${c.pc})`);
      btn.addEventListener('click', () => m.setView(b.getCenter(), 10));
      jumpEl.appendChild(btn);
    }

    // Leaflet mis-measures a container that has not finished layout, and one
    // requestAnimationFrame is not long enough to be sure. Watch the frame.
    let fitted = false;
    ro = new ResizeObserver(() => {
      if (disposed || !map) return;
      map.invalidateSize();
      if (!fitted && frameEl.clientHeight > 50 && homeBounds) {
        map.fitBounds(homeBounds, { padding: [10, 10] });
        fitted = true;
      }
    });
    ro.observe(frameEl);
  })();

  return () => {
    disposed = true;
    ro?.disconnect();
    ro = null;
    drawer?.close();
    drawer = null;
    map?.remove();
    map = null;
    layerByPc.clear();
  };
};
